import type { CrudAction as CrudActionName, FieldAction } from '#layers/access/types/permissions/crud';
import type { AdminPolicyDocument, MonospacePolicyEntitlementResource, RawPolicyContent, RawRulesPolicyContent } from '#layers/access/types/permissions/policy';
import type { AllowedResources, EntitlementKeyParts } from '#layers/access/utils/entitlement-codec';
import type { GlobalCollectionId, GlobalFieldId, GlobalSingleRelationFieldId, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { FIELD_WILDCARD } from '#layers/access/constants/permissions';
import { CrudAction } from '#layers/access/constants/permissions/crud';
import { fieldActions } from '#layers/access/types/permissions/crud';
import { allResourcesAllowed, buildGrantIndex, allowedResourceIds as grantedResourceIds, isGranted, noResourcesAllowed } from '#layers/access/utils/entitlement-codec';
import { getRelationConstraint, getRelationConstraintFields, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { defineWorkspaceEntityHydration, workspaceRuntime } from '#layers/runtime/public';

const FIELD_WILDCARD_SYMBOL = Symbol('wildcard');

/** The entitlement descriptor of a workspace runtime. */
export const entitlementResourcesHydration = defineWorkspaceEntityHydration((store) => store.WorkspaceEntitlementResource.query((q) => q.many({})), { empty: [] });

// Member pages load other users' assignments into the same cache; the filter keeps them out of this list.
export const currentUserRolesHydration = defineWorkspaceEntityHydration((store, { userId }) => store.MonospaceUserRole.query((q) => q.many({
	filter: { userId },
	include: { role: { include: { policies: { include: { policy: {} } } } } },
})), { empty: [] });

function isAdminPolicy(policy: RawPolicyContent): policy is AdminPolicyDocument {
	return policy.access === 'admin';
}

function findActionByName(
	resource: MonospacePolicyEntitlementResource,
	actionName: string,
): MonospacePolicyEntitlementResource['actions'][number] | undefined {
	// Guard against a resource that slipped past validation without an `actions` array:
	// a missing/non-array `actions` must fail closed (return undefined), never throw on the
	// authorization hot path.
	return (resource.actions ?? []).find((action) => action.name === actionName);
}

function descriptorHasAction(resource: MonospacePolicyEntitlementResource, actionName: string): boolean {
	return findActionByName(resource, actionName) !== undefined;
}

function isScoped(resource: MonospacePolicyEntitlementResource, actionName: string): boolean {
	return findActionByName(resource, actionName)?.hasIdentifier ?? false;
}

type FieldPermissionValue = Set<GlobalFieldId> | typeof FIELD_WILDCARD_SYMBOL;

type ItemPermissionMap = Map<GlobalCollectionId, Set<CrudActionName>>;

type FieldPermissionMap = Map<GlobalCollectionId, Map<FieldAction, FieldPermissionValue>>;

export const usePermissionsStore = defineStore('permissions', () => {
	// Without a published runtime, entitlement checks fail closed for non-admins.
	const isReady = computed(() => workspaceRuntime.activeRuntime.value !== null);
	const { data: descriptorResources } = entitlementResourcesHydration.use();
	const { data: userRoles } = currentUserRolesHydration.use();

	function resourceForCollection(collectionId: GlobalCollectionId): MonospacePolicyEntitlementResource | undefined {
		return descriptorResources.value.find((resource) => resource.collectionId === collectionId);
	}

	const policies = computed<RawPolicyContent[]>(() => {
		const result: RawPolicyContent[] = [];

		for (const userRole of userRoles.value) {
			const role = userRole.role;
			if (!role) continue;

			if (role.policies) {
				for (const { policy } of role.policies) {
					if (policy?.content) {
						result.push(policy.content);
					}
				}
			}
		}

		return result;
	});

	const isAdmin = computed(() => policies.value.some((policy) => isAdminPolicy(policy)));

	// Rules policies drive every non-admin gate (item/field rules and entitlements); admin policies
	// carry no rules and are short-circuited by `isAdmin`, so filter them out once here.
	const rulesPolicies = computed(() =>
		policies.value.filter((policy): policy is RawRulesPolicyContent => !isAdminPolicy(policy)),
	);

	const mergedItemPermissions = computed<ItemPermissionMap>(() => {
		const result = new Map<GlobalCollectionId, Set<CrudActionName>>();

		for (const policy of rulesPolicies.value) {
			if (!policy.itemRules) continue;

			for (const [collectionId, rules] of Object.entries(policy.itemRules)) {
				const existing = result.get(collectionId as GlobalCollectionId) ?? new Set();

				if (rules.create !== undefined) existing.add(CrudAction.Create);
				if (rules.read !== undefined) existing.add(CrudAction.Read);
				if (rules.update !== undefined) existing.add(CrudAction.Update);
				if (rules.delete !== undefined) existing.add(CrudAction.Delete);

				result.set(collectionId as GlobalCollectionId, existing);
			}
		}

		return result;
	});

	const mergedFieldPermissions = computed<FieldPermissionMap>(() => {
		const result: FieldPermissionMap = new Map();

		for (const policy of rulesPolicies.value) {
			if (!policy.fieldRules) continue;

			for (const [collectionId, rules] of Object.entries(policy.fieldRules)) {
				const colId = collectionId as GlobalCollectionId;
				let actionMap = result.get(colId);

				if (!actionMap) {
					actionMap = new Map();
					result.set(colId, actionMap);
				}

				for (const action of fieldActions) {
					const fieldRules = rules[action];
					if (!fieldRules) continue;

					const existing = actionMap.get(action);

					// Wildcard always wins (most permissive)
					if (fieldRules === FIELD_WILDCARD || existing === FIELD_WILDCARD_SYMBOL) {
						actionMap.set(action, FIELD_WILDCARD_SYMBOL);
						continue;
					}

					// Merge field IDs (OR logic)
					const fieldIds = existing ?? new Set<GlobalFieldId>();

					for (const fieldId of Object.keys(fieldRules)) {
						fieldIds.add(fieldId as GlobalFieldId);
					}

					actionMap.set(action, fieldIds);
				}
			}
		}

		return result;
	});

	const grantIndex = computed(() => {
		const grants: string[] = [];

		for (const policy of rulesPolicies.value) {
			if (policy.entitlements) grants.push(...policy.entitlements);
		}

		return buildGrantIndex(grants);
	});

	function hasEntitlement(request: EntitlementKeyParts): boolean {
		if (isAdmin.value) return true;
		// Fail closed until the descriptor has loaded: an empty/never-fetched grant set is
		// indistinguishable from "no grants", so deny rather than leak the prior workspace's state.
		if (!isReady.value) return false;
		return isGranted(grantIndex.value, request);
	}

	/** The resources the user may act on for a scoped entitlement (e.g. role:assign). */
	function allowedResourceIds(resource: string, action: string): AllowedResources {
		if (isAdmin.value) return allResourcesAllowed;
		if (!isReady.value) return noResourcesAllowed;
		return grantedResourceIds(grantIndex.value, resource, action);
	}

	function canActionCollection(collectionId: GlobalCollectionId, action: CrudActionName): boolean {
		if (isAdmin.value) return true;
		return mergedItemPermissions.value.get(collectionId)?.has(action) ?? false;
	}

	function canCreate(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Create);
	}

	function canRead(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Read);
	}

	function canUpdate(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Update);
	}

	function canDelete(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Delete);
	}

	function hasAnyAccess(collectionId: GlobalCollectionId): boolean {
		if (isAdmin.value) return true;
		const perms = mergedItemPermissions.value.get(collectionId);
		return perms ? perms.size > 0 : false;
	}

	function canActionField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }, action: FieldAction): boolean {
		if (isAdmin.value) return true;
		const fieldId = typeof field === 'string' ? field : field.id;
		const allowed = mergedFieldPermissions.value.get(collectionId)?.get(action);
		if (!allowed) return false;
		return allowed === FIELD_WILDCARD_SYMBOL || allowed.has(fieldId);
	}

	function canReadField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		if (isAdmin.value) return true;
		const fieldId = typeof field === 'string' ? field : field.id;

		const { findSingleRelationFieldById } = useWorkspaceSchemaStore();
		const relationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);

		if (relationField) {
			return canReadRelationField(relationField);
		}

		return canActionField(collectionId, fieldId, CrudAction.Read);
	}

	function canReadRelationField(field: ResolvedMonospaceSingleRelationField): boolean {
		const { thisSideConstrained, constraint } = getRelationConstraint(field);
		if (!constraint) return false;

		const { constrainedFields } = getRelationConstraintFields(constraint);
		if (constrainedFields.length === 0) return false;

		if (thisSideConstrained) {
			return constrainedFields.every((constrainedField) => canActionField(field.collectionId, constrainedField.id, CrudAction.Read));
		}

		return canRead(field.oppositeCollectionId)
			&& constrainedFields.every((constrainedField) => canActionField(field.oppositeCollectionId, constrainedField.id, CrudAction.Read));
	}

	function canCreateField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		return canActionField(collectionId, field, CrudAction.Create);
	}

	function canUpdateField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		return canActionField(collectionId, field, CrudAction.Update);
	}

	function canCreateFields(collectionId: GlobalCollectionId, fieldIds: GlobalFieldId[]): boolean {
		return fieldIds.every((fieldId) => canCreateField(collectionId, fieldId));
	}

	function canUpdateFields(collectionId: GlobalCollectionId, fieldIds: GlobalFieldId[]): boolean {
		return fieldIds.every((fieldId) => canUpdateField(collectionId, fieldId));
	}

	return {
		isReady,
		resources: descriptorResources,
		resourceForCollection,
		descriptorHasAction,
		isScoped,
		isAdmin,
		hasAnyAccess,
		hasEntitlement,
		allowedResourceIds,
		canActionCollection,
		canCreate,
		canRead,
		canUpdate,
		canDelete,
		canReadField,
		canCreateField,
		canUpdateField,
		canCreateFields,
		canUpdateFields,
	};
});

export type PermissionsStore = ReturnType<typeof usePermissionsStore>;
